import { fetchJSON as fetchAPI } from './client';

export type FoundationModule = {
  dev_id: string;
  name: string;
  service: string;
  port: number;
  route: string;
  status: string;
  score: number;
  findings?: string[];
  extension_points_ok: boolean;
  apis_ok: boolean;
  events_ok: boolean;
  tests_ok: boolean;
};

export type QualityScores = {
  architecture_score: number;
  integration_score: number;
  event_score: number;
  api_score: number;
  persistence_score: number;
  semantic_score: number;
  ai_score: number;
  commercial_score: number;
  observability_score: number;
  security_score: number;
  performance_score: number;
  developer_experience_score: number;
  maintainability_score: number;
  enterprise_readiness_score: number;
  overall_grade: number;
};

export type RiskItem = {
  id: string;
  severity: string;
  category: string;
  module: string;
  description: string;
  mitigation?: string;
  status: string;
};

export type TechnicalDebtItem = {
  id: string;
  module: string;
  description: string;
  impact: string;
  effort: string;
  score: number;
};

export type Recommendation = {
  id: string;
  priority: string;
  area: string;
  module?: string;
  text: string;
};

export type CertificationReport = {
  id: string;
  tenant_id: string;
  scope: string;
  executive_summary: string;
  modules: FoundationModule[];
  scores: QualityScores;
  risks: RiskItem[];
  technical_debt: TechnicalDebtItem[];
  recommendations: Recommendation[];
  decision: string;
  level: string;
  go_no_go: string;
  feature_frozen: boolean;
  production_ready: boolean;
  commercial_ready: boolean;
  platform_complete: boolean;
  certified_at: string;
};

export type ExtensionPoint = {
  dev_id: string;
  name: string;
  description: string;
};

export type CertificationDashboard = {
  tenant_id: string;
  latest_report: CertificationReport;
  modules_passed: number;
  modules_total: number;
  critical_risks: number;
  open_debt_items: number;
  extension_points: ExtensionPoint[];
  generated_at: string;
};

export type CertificationStatus = {
  decision: string;
  level: string;
  go_no_go: string;
  feature_frozen: boolean;
  production_ready: boolean;
  commercial_ready: boolean;
  platform_complete: boolean;
  overall_grade: number;
  certified_at: string;
};

const FOUNDATION_BASE = import.meta.env.VITE_COMMANDCENTER_API ?? '/api/v1/foundation';

function fetchFoundation<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${FOUNDATION_BASE}${path}`, init);
}

export const foundationCertificationApi = {
  dashboard: () => fetchFoundation<CertificationDashboard>('/certification/dashboard'),
  report: () => fetchFoundation<CertificationReport>('/certification/report'),
  scores: () => fetchFoundation<QualityScores>('/certification/scores'),
  modules: () => fetchFoundation<{ modules: FoundationModule[] }>('/certification/modules').then((r) => r.modules),
  risks: () => fetchFoundation<{ risks: RiskItem[] }>('/certification/risks').then((r) => r.risks),
  debt: () => fetchFoundation<{ technical_debt: TechnicalDebtItem[] }>('/certification/debt').then((r) => r.technical_debt),
  recommendations: () => fetchFoundation<{ recommendations: Recommendation[] }>('/certification/recommendations').then((r) => r.recommendations),
  status: () => fetchFoundation<CertificationStatus>('/certification/status'),
  run: () => fetchFoundation<CertificationReport>('/certification/run', { method: 'POST' }),
  extensionPoints: () => fetchFoundation<{ extension_points: ExtensionPoint[] }>('/certification/extensions').then((r) => r.extension_points),
};
